import type { TaxCode } from './taxCodes';

/**
 * Finance invoice foundation — models the legacy FORQ billing_drafts workflow (the proven,
 * DB-persisted one), adapted for FORQ. Two deliberately DECOUPLED axes:
 *   - documentStatus: the invoice document lifecycle (Entwurf → … → Versendet, + Storniert).
 *   - paymentStatus:  a separate payment axis (Offen → Teilbezahlt → Bezahlt) — a sent invoice
 *     can be partially paid, so payment is not part of the document lifecycle.
 * Two numbers: an internal proposal number (RS-26-NNNNNN) from creation, and the legal invoice
 * number (RE-26-NNNNNN) assigned ONCE at "Rechnung erstellt" (after approval) so invoice numbers
 * stay gap-free. Built on the same status-graph pattern as Orders → gets the FORQ Flow trail.
 */
export type InvoiceDocumentStatus =
  | 'draft' // Entwurf
  | 'in_review' // In Prüfung
  | 'approved' // Freigegeben
  | 'invoiced' // Rechnung erstellt (RE-Nummer vergeben)
  | 'sent' // Rechnung versendet
  | 'cancelled'; // Storniert

/** Payment axis — separate from the document lifecycle (legacy: Offen|Teilbezahlt|Bezahlt|…). */
export type InvoicePaymentStatus = 'not_relevant' | 'open' | 'partial' | 'paid';

/** Where the invoice originated — the inbound sources the billing queue unifies. */
export type InvoiceSource = 'service' | 'sales' | 'order' | 'rental' | 'manual';

/** An outgoing document is either a normal invoice or a credit note (Gutschrift/Storno). */
export type InvoiceKind = 'invoice' | 'credit_note';

/** Line kinds mirror the legacy billing_draft_lines line_type set. */
export type InvoiceLineKind =
  | 'labour' // Arbeitszeit
  | 'material' // Material
  | 'travel' // Anfahrt
  | 'service' // Dienstleistung
  | 'machine' // Maschine
  | 'freight' // Fracht
  | 'text' // Freitext
  | 'other'; // Sonstiges

export interface InvoiceLine {
  readonly kind: InvoiceLineKind;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  /**
   * Zeilenrabatt in Prozent. Gibt es, weil ein Rabatt aus Angebot/Vorgang bis 09/2026 auf dem Weg in
   * die Rechnung verschwand -- die Rechnung fiel dadurch ZU HOCH aus. Der Auftragsrabatt des
   * Vorgangs wird beim Abrechnen mit dem Positionsrabatt zu einem Satz je Zeile verrechnet, damit
   * er auch bei gemischten Steuersätzen je Satz richtig ankommt.
   */
  readonly discountPercent: number;
  readonly vatRate: number;
  readonly netAmount: number;
  /**
   * Der Steuerschlüssel der Zeile. Gesetzt = maßgeblich; fehlt er, wird er wie bisher aus dem
   * Satz abgeleitet (Bestandsdaten).
   *
   * WARUM explizit: Bis 09/2026 leitete FORQ den Schlüssel ausschließlich aus dem Prozentsatz ab
   * -- und 0 % bedeutete damit IMMER „steuerfrei". § 13b, innergemeinschaftliche Lieferung,
   * Ausfuhr und die Differenzbesteuerung weisen aber alle 0 % aus und sind trotzdem grundsätzlich
   * verschieden. Ohne dieses Feld waren sie schlicht nicht darstellbar.
   */
  readonly taxCode?: TaxCode | null;
  /**
   * Einkaufspreis des verkauften Gegenstands, EINGEFROREN zum Belegzeitpunkt -- nur bei
   * Differenzbesteuerung (§ 25a UStG) belegt.
   *
   * Gehört an die Zeile und nicht an den Maschinenstamm, weil § 25a Abs. 6 die Aufzeichnung je
   * Gegenstand verlangt: Verkaufspreis, Einkaufspreis, Bemessungsgrundlage. Der Einkaufspreis am
   * Stamm kann sich später ändern; der Beleg muss zeigen, womit gerechnet wurde.
   */
  readonly marginPurchasePrice?: number | null;
  /** Warengruppe der Zeile (Stufe 2b) — optional, aus dem Waren-/Maschinenstamm denormalisiert. */
  readonly warengruppeId?: string | null;
  readonly warengruppeName?: string | null;
}

/**
 * Der Nettobetrag einer Rechnungszeile -- die EINE Rechenregel, die der Demo-Gateway und die
 * Datenbankprozedur `forq.finance_write_lines_and_totals` gleichermaßen abbilden.
 */
export function calculateInvoiceLineNet(
  line: Pick<InvoiceLineInput, 'quantity' | 'unitPrice' | 'discountPercent'>,
): number {
  const quantity = Number(line.quantity) || 0;
  const unitPrice = Number(line.unitPrice) || 0;
  const discount = Math.min(100, Math.max(0, Number(line.discountPercent) || 0));
  return Math.round(quantity * unitPrice * (1 - discount / 100) * 100) / 100;
}

/**
 * Positions- und Belegrabatt zu EINEM Satz je Zeile verrechnen.
 * 10 % auf die Position und 5 % auf den Beleg ergeben zusammen 14,5 %, nicht 15 %.
 */
export function combinedDiscountPercent(positionPercent: number, documentPercent: number): number {
  const position = Math.min(100, Math.max(0, Number(positionPercent) || 0));
  const document = Math.min(100, Math.max(0, Number(documentPercent) || 0));
  const rest = (1 - position / 100) * (1 - document / 100);
  return Math.round((1 - rest) * 10_000) / 100;
}

/**
 * Was eine Journalzeile ausgleicht.
 *   payment       -- geflossenes Geld.
 *   cash_discount -- Skontoabzug: der Kunde hat rechtmässig weniger gezahlt.
 *   write_off     -- Ausbuchung eines Restbetrags (Cent-Differenz, uneinbringlich).
 *
 * Die Trennung ist nicht kosmetisch: `paidAmount` muss dem Kontoauszug entsprechen, sonst geht
 * jeder spätere Bankabgleich schief. Ein Skontoabzug ist kein Geldeingang.
 */
export type InvoicePaymentKind = 'payment' | 'cash_discount' | 'write_off';

export const invoicePaymentKindLabels: Readonly<Record<InvoicePaymentKind, string>> = {
  payment: 'Zahlung',
  cash_discount: 'Skonto',
  write_off: 'Ausbuchung',
};

export interface InvoicePayment {
  readonly id: string;
  readonly kind: InvoicePaymentKind;
  readonly amount: number;
  /** Wertstellung -- wann das Geld da war, NICHT wann jemand es eingetippt hat. */
  readonly valueDate: string;
  /** Zahlungsart als Freitext (Überweisung, bar, EC …). Leer = nicht angegeben. */
  readonly method: string;
  readonly note: string;
  readonly recordedAt: string;
}

/**
 * Eine ausgesprochene Mahnung. Die Beträge sind EINGEFROREN: was gemahnt wurde, muss
 * nachvollziehbar bleiben, auch wenn der offene Betrag sich später ändert.
 */
export interface InvoiceDunning {
  readonly id: string;
  readonly level: number;
  readonly fee: number;
  readonly interest: number;
  /** Der offene Betrag zum Zeitpunkt der Mahnung. */
  readonly openAmount: number;
  readonly issuedDate: string;
  /** Die mit dieser Mahnung gesetzte neue Zahlungsfrist. */
  readonly dueDate: string;
  readonly note: string;
}

export interface Invoice {
  readonly id: string;
  readonly tenantId: string;
  /** Internal proposal number (RS-26-NNNNNN), assigned at creation. */
  readonly proposalNumber: string;
  /** Legal invoice number (RE-26-NNNNNN), null until documentStatus reaches 'invoiced'. */
  readonly invoiceNumber: string | null;
  readonly documentStatus: InvoiceDocumentStatus;
  readonly paymentStatus: InvoicePaymentStatus;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly sourceType: InvoiceSource;
  /** The originating Vorgang/proposal number this invoice was created from. */
  readonly sourceNumber: string;
  readonly kind: InvoiceKind;
  /** For a credit note: the invoice number it credits. */
  readonly creditedInvoiceNumber: string | null;
  /** For an invoice: the credit-note number that reversed it (once storniert/gutgeschrieben). */
  readonly creditNoteNumber: string | null;
  readonly lines: readonly InvoiceLine[];
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  /** Tatsächlich geflossenes Geld -- die Größe, die zum Kontoauszug passen muss. */
  readonly paidAmount: number;
  /**
   * Ausgeglichen: Geld + Skonto + Ausbuchungen. DIESE Größe entscheidet über den Zahlungsstatus.
   * Ohne die Trennung wäre eine mit Skonto beglichene Rechnung entweder ewig offen (wenn nur Geld
   * zählt) oder der Bankabgleich falsch (wenn der Skonto als Geldeingang gälte).
   */
  readonly settledAmount: number;
  /** Das Zahlungsjournal des Belegs, älteste zuerst. */
  readonly payments: readonly InvoicePayment[];
  /** Die ausgesprochenen Mahnungen, älteste zuerst. */
  readonly dunnings: readonly InvoiceDunning[];
  readonly paymentTermDays: number;
  readonly issueDate: string | null;
  /**
   * Leistungsdatum (§ 14 Abs. 4 Nr. 6 UStG) -- Zeitpunkt der Lieferung bzw. sonstigen Leistung.
   * PFLICHTANGABE auf jeder Rechnung. Bis 09/2026 gab es das Feld nicht: der Beleg trug die
   * Beschriftung „Leistungsdatum" und druckte darunter das RECHNUNGSDATUM. Beim Anlegen wird es
   * aus der Quelle vorbelegt (Serviceauftrag: Leistungstag; Vorgang/Verkauf/Miete: Belegdatum)
   * und bleibt im Entwurf änderbar.
   */
  readonly serviceDate: string | null;
  /** Ende des Leistungszeitraums, wenn die Leistung über einen Zeitraum ging (z. B. Miete). */
  readonly serviceEndDate: string | null;
  readonly dueDate: string | null;
  readonly sentAt: string | null;
  readonly paidAt: string | null;
  /** Dunning level (0 = not dunned, 1 = Zahlungserinnerung, 2 = 1. Mahnung, 3 = 2. Mahnung). */
  readonly dunningLevel: number;
  readonly cancelledAt: string | null;
  readonly cancellationReason: string;
  readonly createdAt: string;
  /** Kostenstelle je Beleg (Stufe 2b) — optional. Id für die Zuordnung, Code für DATEV/KOST1, Name für die Anzeige. */
  readonly costCenterId?: string | null;
  readonly costCenterCode?: string | null;
  readonly costCenterName?: string | null;
  /** Belegsprache (de/en) — steuert die Sprache des gedruckten Rechnungs-Belegs. */
  readonly language?: string;
}

export interface InvoiceLineInput {
  readonly kind: InvoiceLineKind;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  /** Zeilenrabatt in Prozent; fehlt er, gilt 0. Siehe InvoiceLine.discountPercent. */
  readonly discountPercent?: number;
  readonly vatRate: number;
  /** Ausdrücklicher Steuerschlüssel; fehlt er, wird er aus dem Satz abgeleitet. */
  readonly taxCode?: TaxCode | null;
  /** Einkaufspreis bei Differenzbesteuerung (§ 25a Abs. 6). */
  readonly marginPurchasePrice?: number | null;
  /** Warengruppe der Zeile (Stufe 2b/3) — optional. Name nur zur Demo-Denormalisierung; Supabase
   *  leitet ihn serverseitig aus warengruppeId ab (finance_invoice_json JOIN). */
  readonly warengruppeId?: string | null;
  readonly warengruppeName?: string;
}

export interface CreateInvoiceInput {
  readonly tenantId: string;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly sourceType: InvoiceSource;
  readonly sourceNumber: string;
  readonly paymentTermDays?: number;
  /** Kostenstelle je Beleg (Stufe 2b) — optional; leer = keine Kostenstelle. */
  readonly costCenterId?: string | null;
  /** Belegsprache der Rechnung (de/en) — kommt beim Abrechnen aus dem Vorgang. Leer = de. */
  readonly language?: string;
  /** Leistungsdatum (§ 14 UStG). Fehlt es, setzt die Ablage das Ausstellungsdatum -- nie leer. */
  readonly serviceDate?: string | null;
  /** Ende des Leistungszeitraums, falls die Leistung über einen Zeitraum ging. */
  readonly serviceEndDate?: string | null;
  readonly lines: readonly InvoiceLineInput[];
}

/** Nachträgliche Änderung eines RechnungsENTWURFS (nur solange documentStatus 'draft', d. h. keine
 *  RE-Nummer vergeben). Danach greift bewusst Storno/Gutschrift. Die Summen rechnet die DB neu. */
export interface UpdateInvoiceInput {
  readonly tenantId: string;
  readonly invoiceId: string;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly paymentTermDays?: number;
  /** Kostenstelle je Beleg (Stufe 2b). Schlüssel gesetzt = ändern (null = leeren); fehlt = unverändert. */
  readonly costCenterId?: string | null;
  /** Leistungsdatum (§ 14 UStG). Schlüssel gesetzt = ändern; fehlt = unverändert. */
  readonly serviceDate?: string | null;
  readonly serviceEndDate?: string | null;
  readonly lines: readonly InvoiceLineInput[];
}

export interface ListInvoicesInput {
  readonly tenantId: string;
  readonly documentStatus?: InvoiceDocumentStatus | 'all';
}

export interface TransitionInvoiceInput {
  readonly tenantId: string;
  readonly invoiceId: string;
  readonly toStatus: InvoiceDocumentStatus;
  readonly reason?: string;
}

export interface RecordPaymentInput {
  readonly tenantId: string;
  readonly invoiceId: string;
  readonly amount: number;
  /** Fehlt sie, gilt 'payment' -- der Normalfall. */
  readonly kind?: InvoicePaymentKind;
  /** Wertstellung (ISO). Fehlt sie, gilt der heutige Tag. */
  readonly valueDate?: string;
  readonly method?: string;
  readonly note?: string;
}

export interface CreateCreditNoteInput {
  readonly tenantId: string;
  /** The invoice to credit/reverse. */
  readonly invoiceId: string;
}

export interface DunInvoiceInput {
  readonly tenantId: string;
  readonly invoiceId: string;
  /** Mahngebühr dieser Stufe. Fehlt sie, gilt 0 -- geraten wird nichts. */
  readonly fee?: number;
  /** Verzugszinsen. Vorschlag aus der Rechnung, im Mahnlauf änderbar. */
  readonly interest?: number;
  /** Die neue Zahlungsfrist (ISO). */
  readonly dueDate?: string;
  readonly note?: string;
}

export interface FinanceGateway {
  listInvoices(input: ListInvoicesInput): Promise<readonly Invoice[]>;
  createInvoice(input: CreateInvoiceInput): Promise<Invoice>;
  /** Ändert einen Rechnungsentwurf (Kunde, Zahlungsziel, Positionen); nur im Status 'draft'. */
  updateInvoice(input: UpdateInvoiceInput): Promise<Invoice>;
  transitionInvoice(input: TransitionInvoiceInput): Promise<Invoice>;
  recordPayment(input: RecordPaymentInput): Promise<Invoice>;
  /** Creates a full credit note for an issued invoice and reverses its open receivable. */
  createCreditNote(input: CreateCreditNoteInput): Promise<Invoice>;
  /** Advances the dunning level of an overdue open invoice by one (max MAX_DUNNING_LEVEL). */
  dunInvoice(input: DunInvoiceInput): Promise<Invoice>;
}

export const MAX_DUNNING_LEVEL = 3;

const dunningLabels = ['Nicht gemahnt', 'Zahlungserinnerung', '1. Mahnung', '2. Mahnung'];
export function dunningLevelLabel(level: number): string {
  return dunningLabels[level] ?? `${level}. Mahnung`;
}

/** Dunning fee accruing at each level (Zahlungserinnerung is free; then escalating). */
export function dunningFee(level: number): number {
  return [0, 0, 5, 10][level] ?? 10;
}

/** Days a still-open invoice is past its due date (0 if not overdue). */
export function daysOverdue(invoice: Invoice, todayIsoDate: string): number {
  if (!invoice.dueDate || invoice.dueDate >= todayIsoDate) return 0;
  return Math.round((Date.parse(todayIsoDate) - Date.parse(invoice.dueDate)) / 86_400_000);
}

/**
 * Document status graph — the single source for the demo gateway, a future Supabase guard and the
 * FORQ Flow controls. Approval gates the legal invoice: only an 'approved' draft can become
 * 'invoiced' (RE number assigned). Storno is possible until the invoice is sent.
 */
export const invoiceTransitions: Readonly<Record<InvoiceDocumentStatus, readonly InvoiceDocumentStatus[]>> = {
  draft: ['in_review', 'cancelled'],
  in_review: ['approved', 'draft', 'cancelled'],
  approved: ['invoiced', 'in_review', 'cancelled'],
  invoiced: ['sent', 'cancelled'],
  sent: [],
  cancelled: [],
};

export function allowedInvoiceTransitionsFor(status: InvoiceDocumentStatus): readonly InvoiceDocumentStatus[] {
  return invoiceTransitions[status];
}

export const invoiceStatusLabels: Readonly<Record<InvoiceDocumentStatus, string>> = {
  draft: 'Entwurf',
  in_review: 'In Prüfung',
  approved: 'Freigegeben',
  invoiced: 'Rechnung erstellt',
  sent: 'Versendet',
  cancelled: 'Storniert',
};

export const paymentStatusLabels: Readonly<Record<InvoicePaymentStatus, string>> = {
  not_relevant: 'Nicht relevant',
  open: 'Offen',
  partial: 'Teilbezahlt',
  paid: 'Bezahlt',
};

/** Permission accepted to move an invoice INTO the given status (checked in UI and, later, the DB). */
export const invoiceTransitionPermission: Readonly<Record<InvoiceDocumentStatus, string>> = {
  draft: 'finance.invoices.edit',
  in_review: 'finance.invoices.edit',
  approved: 'finance.invoices.approve',
  invoiced: 'finance.invoices.issue',
  sent: 'finance.invoices.send',
  cancelled: 'finance.invoices.cancel',
};

/** Button label for the action that reaches the given status. */
export const invoiceActionLabels: Readonly<Record<InvoiceDocumentStatus, string>> = {
  draft: 'Zurück zu Entwurf',
  in_review: 'Zur Prüfung',
  approved: 'Freigeben',
  invoiced: 'Rechnung erstellen',
  sent: 'Versenden',
  cancelled: 'Stornieren',
};

export interface InvoiceTransition {
  readonly toStatus: InvoiceDocumentStatus;
  readonly label: string;
  readonly permission: string;
  readonly tone: 'default' | 'danger';
}

/** The next document-status steps the user is permitted to perform from the invoice's current status. */
export function invoiceFlowTransitions(invoice: Invoice, permissions: ReadonlySet<string>): readonly InvoiceTransition[] {
  return allowedInvoiceTransitionsFor(invoice.documentStatus)
    .map((toStatus) => ({
      toStatus,
      label: invoiceActionLabels[toStatus],
      permission: invoiceTransitionPermission[toStatus],
      tone: toStatus === 'cancelled' ? ('danger' as const) : ('default' as const),
    }))
    .filter((transition) => permissions.has(transition.permission));
}

/** Where an invoice came from — shown on the invoice list and grouped in the Auswertungen. */
export const invoiceSourceLabels: Readonly<Record<InvoiceSource, string>> = {
  service: 'Service',
  sales: 'Verkauf',
  order: 'Vorgang',
  rental: 'Miete',
  manual: 'Manuell',
};

export const lineKindLabels: Readonly<Record<InvoiceLineKind, string>> = {
  labour: 'Arbeitszeit',
  material: 'Material',
  travel: 'Anfahrt',
  service: 'Dienstleistung',
  machine: 'Maschine',
  freight: 'Fracht',
  text: 'Freitext',
  other: 'Sonstiges',
};

/** Local calendar date as `YYYY-MM-DD`, for overdue derivation. */
export function todayIso(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Is `status` overdue? Derived from the due date (legacy: computed, never stored). */
export function isInvoiceOverdue(invoice: Invoice, todayIso: string): boolean {
  return (
    invoice.paymentStatus !== 'paid'
    && invoice.paymentStatus !== 'not_relevant'
    && invoice.dueDate !== null
    && invoice.dueDate < todayIso
  );
}
